
import { Component, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { getAllAlternateUrls, getCanonicalUrl, getSocialMediaMetaData } from '~/helpers/metadata';
import LoginBase from '~/mixins/LoginBase';
import BusinessHeaderSection from '~/components/website/sections/business/BusinessHeaderSection.vue';
import ContentMediaSplitComponent from '~/components/website/ContentMediaSplitComponent.vue';

const cacheStore = namespace('cache');

@Component({
	name: 'ProductsPage',
	components: {
		BusinessHeaderSection,
		ContactFormSection: () => import('~/components/website/sections/ContactFormSection.vue'),
		BusinessTextBlockSection: () =>
			import('~/components/website/sections/business/BusinessTextBlockSection.vue'),
		ContentMediaSplitComponent
	},
	layout: 'plain-company',
	head() {
		const path = `${this.$store.state.host}/business/products`;

		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		const result: any = {
			title: 'Produkte & Inserate | hokify die mobile Jobplattform',
			link: [canonical, ...alternates],
			meta: [
				{ hid: 'robots', name: 'robots', content: 'index, follow' },
				{
					hid: 'description',
					name: 'description',
					content:
						'Mit den Mobile & Social Media Recruiting Lösungen von hokify mehr passende Kandidaten finden. ►  Einfach zu Do-it-Jobs!'
				}
			]
		};

		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				undefined,
				undefined,
				this.$route.query
			)
		);
		return result;
	}
})
export default class ProductsPage extends Mixins(LoginBase()) {
	@cacheStore.State('marketingValues') marketingValues;

	mounted() {
		if (this.$route.hash) {
			this.$scrollTo(this.$route.hash, { offset: -70 });
		}
	}

	arrangeWebinar() {
		this.$trackWebsiteEvent('arrange_hubspot_meeting', { pageElement: 'contact_us' });
		window.open(
			`https://hy.hokify.${this.$store.state.topLevelDomain}/meeting-vereinbaren`,
			'_blank'
		);
	}

	headerItems = {
		image: 'header_products.png',
		imageHeight: 535,
		imageWidth: 700,
		headlineOne: 'Ihre hokify',
		headlineTwo: 'Recruiting Lösung',
		subHeadline:
			'Einfach passende Kandidat:innen finden, dank Stelleninseraten inklusive Social Media, KI-Integration und effizienten Mobile Recruiting Lösungen.'
	};

	productsDataOne = {
		image: 'hokify_socialmedia_ad.png',
		imageMobile: 'hokify_socialmedia_ad.png',
		alt: 'Stelleninserat in der hokify Mobile-App',
		width: 479,
		height: 479
	};

	productsDataTwo = {
		image: 'hokify_socialmedia_pro.png',
		imageMobile: 'hokify_socialmedia_pro.png',
		alt: 'Hokify Socialmedia-Pro',
		width: 479,
		height: 479
	};

	productsDataThree = {
		image: 'hokify_recruiting_assistant.png',
		imageMobile: 'hokify_recruiting_assistant.png',
		alt: 'Hokify Recruiter-Assistent',
		width: 479,
		height: 479
	};

	productsDataFour = {
		image: 'hokify_active_sourcing.png',
		imageMobile: 'hokify_active_sourcing.png',
		alt: 'Stelleninserat in der hokify Mobile-App',
		width: 479,
		height: 479
	};

	get navItems() {
		const items = [
			{
				title: 'Performancestark',
				name: 'Stelleninserat<br class="hidden sm:inline"/> inklusive Social Media',
				id: '#job-ad',
				image: 'social-media-ad'
			},
			{
				title: 'Individuell',
				name: 'Individuelle Social Media <br class="hidden sm:inline"/> Kampagne',
				id: '#social-media-campaigns',
				image: 'social-media-pro'
			},
			{
				title: 'Effizient',
				name: 'Recruiting<br class="hidden sm:inline"/> Assistant',
				id: '#recruiting-assistant',
				image: 'recruiting-assistant'
			}
		];

		if (this.$store.state.topLevelDomain === 'at') {
			items.push({
				title: 'Gezielt',
				name: '<span class="italic">hy!Active</span>',
				id: '#active-sourcing',
				image: 'active-sourcing'
			});
		}

		return items;
	}

	jobAdCard = {
		type: 'img',
		mediaPosition: 'right',
		src: 'img/business/pricing.png',
		title: 'Einfach Inserat erstellen und loslegen!',
		text: 'Das optimale Angebot für Ihre Bedürfnisse. Ob KMU, Großunternehmen oder Personaldienstleister, hokify ist Ihre Lösung.',
		buttonText: 'Jetzt Inserat schalten',
		buttonLink: '/business/pricing',
		textCenter: true,
		bgColor: 'main-business',
		largeTitle: true
	};

	get highlightsData() {
		return [
			{
				title: 'Social Media & Mobile Recruiting',
				decription:
					'Jedes hokify Stelleninserat wird auf relevanten Social Media Kanälen strategisch platziert. So erhalten Sie einen Social Media Boost, mit dem Ihre Kandidat:innen zielgruppengenau erreicht werden. Das Setup und die Optimierung übernehmen wir für Sie.',
				features: {
					one: 'Die Power von Social Media',
					two: 'Zielgruppengenau für Do-it-Jobs'
				},
				image: 'highlight_socialmedia.png'
			},
			{
				title: 'BMS für KMUs ',
				decription:
					'hokify bietet Recruiting-Lösungen für alle Unternehmensgrößen an. Mit dem eigenen hokify Bewerbermanagementsystem (BMS) können KMUS ganz einfach Bewerbungen schnell und effizient managen.',
				features: {
					one: 'Für alle Unternehmensgrößen',
					two: 'Einfach und effizient'
				},
				image: 'highlight_bms.png'
			},
			{
				title: 'Integrationsexperte',
				decription:
					'Mit über 750 Schnittstellen können sie hokify nahtlos in Ihre bestehenden Bewerbermanagementsysteme integrieren. Damit wird sichergestellt, dass Kandidat:innen sich mit ihrem hokify Profil problemlos bei Ihnen bewerben können und Sie alle Bewerbungen in Ihrem Bewerbermanagementsystem einfach und schnell verwalten können.',
				features: {
					one: 'Individuell',
					two: 'Nahtlose Integration'
				},
				image: 'highlight_integrations.png'
			},
			{
				title: 'Transparentes Recruiting',
				decription:
					'Der hokify Recruiting- und Bewerbungsprozess ermöglicht eine transparente und einfache KPI-Auswertung. So sehen Sie ganz genau, welche Maßnahmen Sie am effizientesten zum Ziel führen – mit umfassendem Tracking der gesamten Candidate Journey und mehr! ',
				features: {
					one: 'Einfach transparent',
					two: 'KPI-basiert'
				},
				image: 'highlight_transparent_recruiting.png'
			}
		];
	}
}
